/**
 * Management Approval Queue (Feb 2026)
 *
 * Admin-only inbox. Aggregates every pending SO / ESO / PO / WO into
 * one screen so leadership can approve or reject-with-comments without
 * hopping between individual pages.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { PoAmendGstComparison, LegacyAmendChip } from "@/components/approvals/PoAmendGstComparison";
import { approveBlocked, approveConfirmText, chargeText, fieldLabel, gstIntegrity, INTEGRITY_MESSAGE, isStaleGstError, STALE_MESSAGE } from "@/lib/poApprovalGst";
import { getUser } from "@/lib/secureStorage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ShieldCheckIcon, CheckCircleIcon, XCircleIcon, EyeIcon,
  ArrowsClockwiseIcon, ClockCounterClockwiseIcon, DownloadSimpleIcon,
} from "@phosphor-icons/react";

const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Mar 1, 2026 · Currency-aware value formatter. Uses the doc's foreign
// currency when set (USD/EUR/…), otherwise falls back to ₹.
// DP-EGSTI-AUTH-CONFIRM-VALUE-01 · Mar 2026 — added a strict
// unavailable-state fail-safe so an FC-priced row missing both
// `value_usd` and `value_fc` renders as "$—" (not the misleading
// "$0.00" it used to). Preserves every working payload contract:
//   · SO / PO / WO / GST / PI  → `value_ccy == "INR"` → INR branch
//   · ESO / EPI / ECI          → `value_ccy == "USD"` (or FC) with
//                                 `value_usd` populated → FC branch
// A legitimate numeric 0 (e.g. `value_usd: 0`) still renders as `$0.00`
// because the guard fires only on `null` / `undefined` (i.e. genuine
// absence of the canonical field). Numeric 0 remains numeric 0.
const CCY_SYM = { USD: "$", EUR: "€", GBP: "£", AED: "AED ", INR: "₹", JPY: "¥", CNY: "¥", AUD: "A$", CAD: "C$", SGD: "S$", CHF: "CHF " };
const fmtValue = (r) => {
  const ccy = (r.value_ccy || "INR").toUpperCase();
  if (ccy === "INR" || !ccy) {
    if (r.value_inr === null || r.value_inr === undefined) return "—";
    return fmtInr(r.value_inr);
  }
  const sym = CCY_SYM[ccy] || `${ccy} `;
  const raw = r.value_usd !== null && r.value_usd !== undefined
    ? r.value_usd
    : (r.value_fc !== null && r.value_fc !== undefined ? r.value_fc : null);
  if (raw === null) return `${sym}—`;
  const n = Number(raw);
  return `${sym}${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const TYPE_TONE = {
  SO:  "border-purple-500/40 text-purple-300 bg-purple-500/10",
  ESO: "border-blue-500/40   text-blue-300   bg-blue-500/10",
  PO:  "border-amber-500/40  text-amber-300  bg-amber-500/10",
  WO:  "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  PI:  "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10",
  EPI: "border-cyan-500/40   text-cyan-300   bg-cyan-500/10",
  ECI: "border-pink-500/40   text-pink-300   bg-pink-500/10",
  GST: "border-lime-500/40   text-lime-300   bg-lime-500/10",
};

// PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Feb 2026 — distinguishes a
// New PO Authorisation row from a PO Amendment Re-authorisation row.
// Presence of `r.amendment` (populated only by the backend queue
// projection when `active_amendment_ref` is set + amendment state ==
// "Pending Re-authorisation") flips the row into amendment mode.
const isAmendmentRow = (r) => !!(r && r.doc_type === "PO" && r.amendment);
const rowKindLabel = (r) => (isAmendmentRow(r) ? "PO AMENDMENT RE-AUTHORISATION" : ({
  SO: "NEW SO AUTHORISATION", ESO: "NEW ESO AUTHORISATION", PO: "NEW PO AUTHORISATION",
  WO: "NEW WO AUTHORISATION", PI: "PROFORMA AUTHORISATION",
  EPI: "EXPORT PROFORMA AUTHORISATION", ECI: "EXPORT INVOICE AUTHORISATION",
  GST: "GST INVOICE AUTHORISATION",
}[r.doc_type] || `${r.doc_type} AUTHORISATION`));

export default function ManagementApprovals() {
  const user = getUser();
  const [data, setData] = useState({ count: { total: 0, SO: 0, ESO: 0, PO: 0, WO: 0, PI: 0, EPI: 0, ECI: 0, GST: 0 }, items: [] });
  const [reversed, setReversed] = useState({ count: { total: 0 }, items: [] });
  const [view, setView] = useState("pending");  // "pending" | "reversed"
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectText, setRejectText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [pendResp, revResp] = await Promise.all([
        api.get("/approvals/queue"),
        api.get("/approvals/reversed"),
      ]);
      setData(pendResp.data);
      setReversed(revResp.data || { count: { total: 0 }, items: [] });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load authorisation queue"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const activeData = view === "reversed" ? reversed : data;
  const filtered = useMemo(() => (activeData.items || []).filter(r => {
    if (typeFilter !== "all" && r.doc_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![r.doc_no, r.party, r.reference].some(v => (v || "").toLowerCase().includes(q))) return false;
    }
    return true;
  }), [activeData, typeFilter, search]);

  // PO-GST-01 · P2-D2 · stale submitted GST (409) → dedicated guidance; never auto-reject / recalc.
  const [staleFor, setStaleFor] = useState(null);   // { id, doc_no, amendment_no } — shown for row AND detail entry points
  const approve = async (r) => {
    if (approveBlocked(r)) { toast.error(INTEGRITY_MESSAGE, { duration: 10000 }); return; }   // P2-D2-FIX01
    const base = `Authorise ${r.doc_no} (${r.doc_type})?\n\nParty: ${r.party}\nQty: ${fmtInt(r.qty)}\nValue: ${fmtValue(r)}`;
    if (!window.confirm(isAmendmentRow(r) ? approveConfirmText(r, base) : base)) return;
    try {
      await api.post(`/approvals/${r.doc_type}/${r.id}/approve`);
      toast.success(`${r.doc_no} · authorised`);
      setStaleFor(s => (s && s.id === r.id ? null : s)); setDetail(null); load();
    } catch (e) {
      if (isStaleGstError(e)) {
        setStaleFor({ id: r.id, doc_no: r.doc_no, amendment_no: r.amendment?.amendment_no || "" });
        toast.error(STALE_MESSAGE, { duration: 12000 });
      } else {
        toast.error(apiErrorMessage(e, "Authorise failed"));
      }
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    const comments = (rejectText || "").trim();
    if (!comments) return toast.error("Comments are required to reject");
    try {
      await api.post(`/approvals/${rejectFor.doc_type}/${rejectFor.id}/reject`, { comments });
      toast.success(`${rejectFor.doc_no} · rejected`);
      setRejectFor(null); setRejectText(""); setDetail(null); load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reject failed"));
    }
  };

  // Sep 05, 2026 · CSV export — writes the CURRENTLY VISIBLE rows
  // (respects tab + type filter + search) so finance can email a
  // targeted slice in one click. RFC-4180 minimal quoting.
  const downloadCsv = () => {
    if (!filtered.length) return toast("Nothing to export");
    const HEADERS = ["Type", "Doc No", "Date", "Party", "Reference", "Lines",
                     "Qty", "Value INR", "Value CCY", "Value FC",
                     "Delivery Date", "Status", "Created At", "Created By"];
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map(r => [
      r.doc_type, r.doc_no, r.date, r.party, r.reference, r.line_count,
      r.qty, r.value_inr, r.value_ccy,
      r.value_usd ?? r.value_fc ?? "",
      r.delivery_date, r.status, r.created_at, r.created_by,
    ].map(esc).join(","));
    const csv = "\uFEFF" + [HEADERS.join(","), ...rows].join("\n");
    const stamp = new Date().toISOString().slice(0, 10);
    const label = view === "reversed" ? "reversed" : "pending";
    const suffix = typeFilter === "all" ? "all" : typeFilter;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"),
      { href: url, download: `approvals_${label}_${suffix}_${stamp}.csv` });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} row${filtered.length === 1 ? "" : "s"}`);
  };

  if (user?.role !== "Administrator") {
    return (
      <div className="p-8 text-center">
        <ShieldCheckIcon size={48} weight="light" className="mx-auto text-zinc-500 mb-3" />
        <div className="text-lg font-semibold text-white">Restricted</div>
        <div className="text-sm text-zinc-500 mt-1">The Management Authorisation Queue is Administrator-only.</div>
      </div>
    );
  }

  const c = data.count || {};
  return (
    <div className="p-6 space-y-5" data-testid="mgmt-approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><ShieldCheckIcon size={11} weight="bold" />Administrator · Management Authorisation Queue</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Management Authorisation Queue</h1>
          <div className="text-xs text-zinc-500 mt-1">Every SO, ESO, PO and Work Order waiting for your sign-off · auto-refreshes every 30s</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="approvals-refresh">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      {staleFor && (
        <div data-testid="approvals-gst-stale-banner" className="border border-rose-500/50 bg-rose-500/10 text-rose-200 text-xs rounded-sm px-3 py-2 flex items-start justify-between gap-3">
          <span><span className="font-mono font-semibold">{staleFor.doc_no} · {staleFor.amendment_no}</span> — {STALE_MESSAGE}</span>
          <button data-testid="approvals-gst-stale-dismiss" onClick={() => setStaleFor(null)} className="text-rose-300 hover:text-white text-[10px] font-mono uppercase">Dismiss</button>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-[var(--border)]">
        <button
          onClick={() => { setView("pending"); setTypeFilter("all"); }}
          data-testid="approvals-tab-pending"
          className={`px-4 py-2 text-sm font-mono uppercase tracking-widest border-b-2 transition-colors ${view === "pending" ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
        >Pending Authorisation · {fmtInt((data.count || {}).total)}</button>
        <button
          onClick={() => { setView("reversed"); setTypeFilter("all"); }}
          data-testid="approvals-tab-reversed"
          className={`px-4 py-2 text-sm font-mono uppercase tracking-widest border-b-2 transition-colors ${view === "reversed" ? "border-red-400 text-red-200" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
        >Reversed · {fmtInt((reversed.count || {}).total)}</button>
      </div>

      {/* KPI strip · per-type pending counts */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
        {(view === "reversed"
          ? [
              { k: "all", label: "Total Reversed", value: (reversed.count || {}).total, tone: "text-white" },
              { k: "SO",  label: "Sales Orders",   value: (reversed.count || {}).SO,   tone: "text-purple-300" },
              { k: "ESO", label: "Export Orders",  value: (reversed.count || {}).ESO,  tone: "text-blue-300" },
              { k: "PO",  label: "Purchase Orders",value: (reversed.count || {}).PO,   tone: "text-amber-300" },
              { k: "WO",  label: "Work Orders",    value: (reversed.count || {}).WO,   tone: "text-emerald-300" },
              { k: "PI",  label: "Proforma Inv.",  value: (reversed.count || {}).PI,   tone: "text-fuchsia-300" },
              { k: "GST", label: "GST Tax Inv.",   value: (reversed.count || {}).GST,  tone: "text-lime-300" },
              { k: "DC",  label: "Del. Challans",  value: (reversed.count || {}).DC,   tone: "text-orange-300" },
            ]
          : [
              { k: "all", label: "Total Pending", value: c.total, tone: "text-white" },
              { k: "SO",  label: "Sales Orders",   value: c.SO,   tone: "text-purple-300" },
              { k: "ESO", label: "Export Orders",  value: c.ESO,  tone: "text-blue-300" },
              { k: "PO",  label: "Purchase Orders",value: c.PO,   tone: "text-amber-300" },
              { k: "WO",  label: "Work Orders",    value: c.WO,   tone: "text-emerald-300" },
              { k: "PI",  label: "Proforma Inv.",  value: c.PI,   tone: "text-fuchsia-300" },
              { k: "EPI", label: "Export Proforma",value: c.EPI,  tone: "text-cyan-300" },
              { k: "ECI", label: "Export Comm. Inv.", value: c.ECI, tone: "text-pink-300" },
              { k: "GST", label: "GST Tax Inv.",   value: c.GST,  tone: "text-lime-300" },
            ]
        ).map(t => (
          <button key={t.k} onClick={() => setTypeFilter(t.k)}
            data-testid={`approvals-tile-${t.k}`}
            className={`text-left rounded-sm border p-4 transition-colors ${typeFilter === t.k ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] bg-[var(--bg-1)] hover:bg-[var(--bg-2)]/50"}`}>
            <div className="overline text-[10px]">{t.label}</div>
            <div className={`font-display font-black text-2xl mt-1 tabular-nums ${t.tone}`}>{fmtInt(t.value)}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doc no / party / reference…"
          className="h-9 flex-1 max-w-md px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-white focus:outline-none focus:border-[var(--accent)]"
          data-testid="approvals-search" />
        <button
          onClick={downloadCsv}
          disabled={!filtered.length}
          data-testid={`approvals-export-csv-${view}`}
          title="Download the currently visible rows as a CSV — respects tab + type filter + search"
          className="ml-auto h-9 px-3 border border-[var(--border)] hover:border-emerald-400/60 hover:bg-emerald-500/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <DownloadSimpleIcon size={12} weight="bold" /> CSV
        </button>
        <span className="text-xs text-zinc-500 font-mono">{filtered.length} item{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="approvals-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3 w-20">Type</th>
              <th className="text-left px-4 py-3">Doc No</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Party</th>
              <th className="text-left px-4 py-3">Reference</th>
              <th className="text-right px-4 py-3">Lines</th>
              <th className="text-right px-4 py-3">Qty</th>
              <th className="text-right px-4 py-3">Value</th>
              <th className="text-left px-4 py-3">Delivery</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={`${r.doc_type}-${r.id}-${(r.amendment && r.amendment.amendment_no) || "new"}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`approvals-row-${r.doc_type}-${r.id}${isAmendmentRow(r) ? `-${r.amendment.amendment_no}` : ""}`}>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest w-fit ${TYPE_TONE[r.doc_type] || ""}`}>{r.doc_type}</span>
                    {isAmendmentRow(r) && (
                      <span data-testid={`approvals-row-kind-${r.doc_type}-${r.id}`} className="inline-flex items-center px-2 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 text-[9px] font-mono uppercase tracking-widest w-fit">Amendment</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-white">
                  <div className="flex flex-col">
                    <span>{r.doc_no}</span>
                    {isAmendmentRow(r) && (
                      <span className="text-[10px] font-mono text-cyan-300">Revision {Number(r.amendment.revision || 0)} · {r.amendment.amendment_no}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{r.date || "—"}</td>
                <td className="px-4 py-3 text-zinc-300">{r.party}</td>
                <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{r.reference || "—"}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-400">{r.line_count}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtInt(r.qty)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-300" data-testid={`approvals-value-${r.doc_type}-${r.id}`} title={r.value_ccy && r.value_ccy !== "INR" ? `INR @ FX: ${fmtInr(r.value_inr)}` : ""}>{fmtValue(r)}</td>
                <td className="px-4 py-3 font-mono text-amber-300 text-xs">{r.delivery_date || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button title="Quick view" onClick={() => setDetail(r)} data-testid={`approvals-view-${r.doc_type}-${r.id}`}
                    className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-[var(--accent)]"><EyeIcon size={14} /></button>
                  {view === "pending" && (
                    <>
                      <button title={approveBlocked(r) ? INTEGRITY_MESSAGE : "Authorise"} onClick={() => approve(r)} disabled={approveBlocked(r)}
                        data-testid={`approvals-approve-${r.doc_type}-${r.id}`}
                        className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"><CheckCircleIcon size={14} weight="bold" /></button>
                      {approveBlocked(r) && <span data-testid={`approvals-integrity-chip-${r.doc_type}-${r.id}`} title={gstIntegrity(r).issues.join("\n")} className="text-[9px] font-mono uppercase text-amber-300">GST data check</span>}
                      <button title="Reject with Comments" onClick={() => { setRejectFor(r); setRejectText(""); }} data-testid={`approvals-reject-${r.doc_type}-${r.id}`}
                        className="p-1.5 rounded-sm hover:bg-red-500/10 text-red-400"><XCircleIcon size={14} weight="bold" /></button>
                    </>
                  )}
                  {view === "reversed" && (
                    <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-300/70" title={`Voided ${r.created_at || ""} by ${r.created_by || "—"}`}>Reversed</span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={10} className="text-center py-16 text-zinc-500">
                <ClockCounterClockwiseIcon size={28} weight="light" className="mx-auto mb-2 text-zinc-600" />
                Nothing waiting for your sign-off — good work.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Quick-view modal — mirrors the Sales Orders preview modal
          layout so managers see the exact same details they would on
          the source screen. */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="approvals-detail" description="Management authorisation action — review, authorise or reject the pending document.">
          {detail && (() => {
            const totQty = (detail.items || []).reduce((a, li) => a + Number(li.qty || li.order_qty || 0), 0);
            const totVal = (detail.items || []).reduce((a, li) => a + Number(li.value || (Number(li.qty || li.order_qty || 0) * Number(li.rate || 0))), 0);
            // Mar 1, 2026 · Currency symbol resolved from the doc's value_ccy so
            // ESO priced in USD/EUR/… stops rendering as ₹ across the modal.
            const CCY_SYM = { USD: "$", EUR: "€", GBP: "£", AED: "AED ", INR: "₹", JPY: "¥", CNY: "¥", AUD: "A$", CAD: "C$", SGD: "S$", CHF: "CHF " };
            const ccySym = CCY_SYM[detail.value_ccy] || (detail.value_ccy ? `${detail.value_ccy} ` : "₹");
            // UAT-EG-01 · Jun 2026 — authoritative doc-type → approval-view
            // mapping. Every supported type gets its own title; an UNKNOWN
            // type falls back to a safe generic "<TYPE> Approval" label —
            // NEVER to "Work Order". Commercial-invoice types (ECI/EGST/
            // PI/EPI) drop WO production semantics (Buffer/Rejection/Total).
            const TITLE = {
              SO: "Sales Order", ESO: "Export Sales Order", PO: "Purchase Order",
              WO: "Work Order", ECI: "Export Commercial Invoice",
              EGST: "Export GST Invoice", EGSTI: "Export GST Invoice",
              PI: "Proforma Invoice", EPI: "Export Proforma Invoice",
            };
            const label = TITLE[detail.doc_type] || `${detail.doc_type || "Document"} Approval`;
            const isCommercialInv = ["ECI", "EGST", "EGSTI", "PI", "EPI"].includes(detail.doc_type);
            const partyLbl = detail.doc_type === "PO" ? "Supplier" : "Customer";
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
                    <span>{label} — {detail.doc_no}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${TYPE_TONE[detail.doc_type] || ""}`}>{detail.status || "Draft"}</span>
                    {isAmendmentRow(detail) && (detail.amendment.state === "Pending Re-authorisation") && (
                      <span
                        data-testid="approvals-detail-amend-pending-badge"
                        className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest border-amber-500/50 bg-amber-500/10 text-amber-300"
                        title="This Purchase Order Amendment is awaiting Management Authorisation. The effective PO remains Authorised at the current revision until the Amendment is Authorised."
                      >
                        Amendment · Pending Management Authorisation
                      </span>
                    )}
                    {isAmendmentRow(detail) && (
                      <span data-testid="approvals-detail-kind" className="inline-flex items-center px-2 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 text-[10px] font-mono uppercase tracking-widest">{rowKindLabel(detail)}</span>
                    )}
                    {isAmendmentRow(detail) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Revision {Number(detail.amendment.revision || 0)} · {detail.amendment.amendment_no}</span>
                    )}
                    {isAmendmentRow(detail) && !detail.amendment.gst_comparison && <LegacyAmendChip />}
                    {detail.priority && detail.priority !== "Normal" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest">{detail.priority}</span>
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="overline text-[10px] mb-0.5">{partyLbl} Name</div>
                    <div className="font-semibold text-sm">{detail.party || "—"}</div>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-0.5">{detail.doc_type === "PO" ? "Source PR / MRP" : detail.doc_type === "WO" ? "Source SO/ESO" : isCommercialInv ? "Buyer PO / Ref" : "Customer PO No"}</div>
                    <div className="font-mono text-sm">{detail.reference || "—"}</div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs mb-4">
                  {/* Mar 1, 2026 · ESO is priced in foreign currency (USD/EUR/…).
                      Pick the symbol from `value_ccy` so rate + total + line-value
                      no longer masquerade as INR. */}
                  {(() => { return null; })()}
                  <div><div className="overline text-[10px] mb-0.5">{isCommercialInv ? "Invoice Date" : "Order Date"}</div><div className="font-mono">{detail.date || "—"}</div></div>
                  {!isCommercialInv && (
                    <div><div className="overline text-[10px] mb-0.5">Delivery Date</div><div className="font-mono text-amber-300">{detail.delivery_date || "—"}</div></div>
                  )}
                  <div><div className="overline text-[10px] mb-0.5">{isCommercialInv ? "Invoice Qty" : "Total Qty (m)"}</div><div className="font-mono font-semibold">{fmtInt(totQty || detail.qty)}</div></div>
                  <div><div className="overline text-[10px] mb-0.5" data-testid="approvals-detail-value-label">{isAmendmentRow(detail) && detail.amendment.gst_comparison ? "Goods Value (excl. GST)" : `Total Value (${ccySym === "₹" ? "₹" : detail.value_ccy || "₹"})`}</div><div className="font-mono font-semibold">{ccySym}{Number(ccySym === "₹" ? (totVal || detail.value_inr || 0) : (detail.value_usd || totVal || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Line Items</div><div className="font-mono">{detail.line_count}</div></div>
                  {detail.amendments_count !== undefined && (
                    <div><div className="overline text-[10px] mb-0.5">Amendments</div><div className="font-mono">{detail.amendments_count}</div></div>
                  )}
                  <div><div className="overline text-[10px] mb-0.5">Created</div><div className="font-mono text-[11px]">{(detail.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                  {detail.value_ccy && detail.value_ccy !== "INR" && (
                    <div><div className="overline text-[10px] mb-0.5">Value INR (@ FX)</div><div className="font-mono text-zinc-400">₹{Number(detail.value_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                  )}
                </div>

                {/* EGSTI-MA-01B · Jun 2026 — Export GST Invoice statutory
                    approval block. All values are authoritative reads from
                    the EGSTI read model / sealed lineage resolver (backend
                    `egsti_meta`). Frontend performs NO tax computation. */}
                {detail.doc_type === "EGST" && detail.egsti_meta && (
                  <div data-testid="approvals-egsti-meta" className="grid grid-cols-4 gap-3 text-xs mb-4 border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-4 py-3">
                    <div><div className="overline text-[10px] mb-0.5">Source ECI No</div><div className="font-mono text-emerald-300" data-testid="approvals-egsti-eci-no">{detail.egsti_meta.source_eci_no || "—"}</div></div>
                    <div><div className="overline text-[10px] mb-0.5">Linked EDC No</div><div className="font-mono text-sky-300" data-testid="approvals-egsti-edc-no">{detail.egsti_meta.edc_no || "—"}</div></div>
                    <div><div className="overline text-[10px] mb-0.5">Export / GST Type</div><div className="font-mono" data-testid="approvals-egsti-export-type">{detail.egsti_meta.export_type === "IGST" ? "Export w/ IGST" : "Export under LUT"}</div></div>
                    <div><div className="overline text-[10px] mb-0.5">Currency</div><div className="font-mono">{detail.egsti_meta.currency || detail.value_ccy || "—"}</div></div>
                    <div><div className="overline text-[10px] mb-0.5">Taxable Value ({detail.egsti_meta.currency || detail.value_ccy})</div><div className="font-mono font-semibold" data-testid="approvals-egsti-taxable">{Number(detail.egsti_meta.taxable_value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                    <div><div className="overline text-[10px] mb-0.5">IGST Amount ({detail.egsti_meta.currency || detail.value_ccy})</div><div className="font-mono font-semibold" data-testid="approvals-egsti-igst">{Number(detail.egsti_meta.igst_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                  </div>
                )}


                {/* PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Amendment
                    delta view — Management sees prev → proposed at
                    header and line level BEFORE authorising. Compact
                    inline table (owner directive H). */}
                {isAmendmentRow(detail) && (
                  <div data-testid="approvals-detail-amendment-delta" className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-3 mb-4">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="overline text-cyan-300">Amendment Delta · {detail.amendment.amendment_no}</div>
                      <div className="text-[10px] font-mono text-zinc-400">Submitted by <span className="text-white">{detail.amendment.submitted_by || "—"}</span> · <span className="text-zinc-500">{(detail.amendment.submitted_at || "").slice(0, 19).replace("T", " ")}</span></div>
                    </div>
                    <div className="text-xs text-zinc-300 mb-3"><span className="text-zinc-500 mr-1">Amendment Reason:</span><span className="text-amber-200">{detail.amendment.reason || "—"}</span></div>
                    {(() => {
                      const dHeader = (detail.amendment.diff && detail.amendment.diff.header) || [];
                      const dLines = (detail.amendment.diff && detail.amendment.diff.lines) || [];
                      const fmt = (v) => v === "" || v == null ? "—"
                        : Array.isArray(v) ? (v.length ? v.map(chargeText).join("; ") : "None")
                        : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v);
                      if (dHeader.length === 0 && dLines.length === 0) {
                        return <div className="text-[11px] text-zinc-500">No field-level changes recorded on this amendment.</div>;
                      }
                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-[var(--bg-2)]/50 text-[10px] uppercase tracking-widest text-zinc-500">
                              <tr>
                                <th className="px-2 py-1.5 text-left w-24">Scope</th>
                                <th className="px-2 py-1.5 text-left">Field</th>
                                <th className="px-2 py-1.5 text-left">RM / Line</th>
                                <th className="px-2 py-1.5 text-right">Current Authorised</th>
                                <th className="px-2 py-1.5 text-right">Proposed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dHeader.map((d, i) => (
                                <tr key={`h-${i}`} className="border-t border-[var(--border)]" data-testid={`approvals-delta-header-${d.field}`}>
                                  <td className="px-2 py-1.5 font-mono text-zinc-500">Header</td>
                                  <td className="px-2 py-1.5 font-mono text-zinc-300" title={d.field}>{fieldLabel(d.field)}</td>
                                  <td className="px-2 py-1.5 text-zinc-500">—</td>
                                  <td className="px-2 py-1.5 text-right font-mono text-rose-300">{fmt(d.prev)}</td>
                                  <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(d.new)}</td>
                                </tr>
                              ))}
                              {dLines.map((d, i) => {
                                const priorLine = (detail.amendment.prior_lines || []).find(pl => pl.line_no === d.line_no) || {};
                                const liveLine = (detail.items || []).find(x => Number(x.line_no || 0) === Number(d.line_no)) || {};
                                const rmLabel = liveLine.product_code || priorLine.rm_code || `#${d.line_no}`;
                                return (
                                  <tr key={`l-${i}`} className="border-t border-[var(--border)]" data-testid={`approvals-delta-line-${d.line_no}-${d.field}`}>
                                    <td className="px-2 py-1.5 font-mono text-zinc-500">Line {d.line_no}</td>
                                    <td className="px-2 py-1.5 font-mono text-zinc-300" title={d.field}>{fieldLabel(d.field)}</td>
                                    <td className="px-2 py-1.5 font-mono text-[var(--accent)]">{rmLabel}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-rose-300">{fmt(d.prev)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(d.new)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                    <div className="text-[10px] font-mono text-zinc-500 mt-2">
                      Authorising promotes the proposed values to Revision {Number(detail.amendment.revision || 0) + 1} atomically. Rejecting returns the amendment to Draft for correction — the effective PO remains Authorised at Revision {Number(detail.amendment.revision || 0)}.
                    </div>
                  </div>
                )}

                {isAmendmentRow(detail) && staleFor?.id === detail.id && (
                  <div data-testid="approvals-gst-stale-msg" className="mb-4 border border-rose-500/50 bg-rose-500/10 text-rose-200 text-xs rounded-sm px-3 py-2">{STALE_MESSAGE}</div>
                )}
                {approveBlocked(detail) && (
                  <div data-testid="approvals-gst-integrity-warning" className="mb-4 border border-amber-500/50 bg-amber-500/10 text-amber-200 text-xs rounded-sm px-3 py-2">
                    <div className="font-semibold mb-1">{INTEGRITY_MESSAGE}</div>
                    <ul className="list-disc pl-4 font-mono text-[11px]">{gstIntegrity(detail).issues.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {isAmendmentRow(detail) && <PoAmendGstComparison row={detail} />}

                <div className="overline mb-2">Line Items</div>
                {/* PO-2B2-UAT-D1 · Feb 2026 — Amendment-scoped Line Items
                    view: renders Current vs Proposed Buffer/Total per
                    line. Values come from `detail.amendment.proposed_lines`
                    which is computed server-side by the canonical Phase
                    2B.1 merge helper — no client-side quantity math.
                    Non-amendment rows fall through to the shared table
                    below unchanged. */}
                {isAmendmentRow(detail) ? (
                  <div className="border border-cyan-500/40 rounded-sm overflow-x-auto" data-testid="approvals-amendment-line-items">
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--bg-2)]">
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                          <th className="px-2 py-2 text-left">#</th>
                          <th className="px-2 py-2 text-left">RM Code</th>
                          <th className="px-2 py-2 text-right">Required</th>
                          <th className="px-2 py-2 text-right text-zinc-400" title="Currently authorised Adjust quantity">Cur Adjust</th>
                          <th className="px-2 py-2 text-right text-zinc-400" title="Currently authorised total (qty_required + cur buffer)">Cur Total</th>
                          <th className="px-2 py-2 text-right text-cyan-300" title="Proposed total after amendment (qty_required + proposed buffer)">Prop Total</th>
                          <th className="px-2 py-2 text-right text-zinc-400">Cur Rate</th>
                          <th className="px-2 py-2 text-right text-cyan-300">Prop Rate</th>
                          <th className="px-2 py-2 text-right">UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.amendment.proposed_lines || []).map((pl) => {
                          const changed = new Set(pl.changed_fields || []);
                          const bufferChanged = changed.has("qty_adjustment");
                          const rateChanged = changed.has("unit_cost");
                          const totalChanged = bufferChanged;
                          const propTotCls = totalChanged ? "text-cyan-300 bg-cyan-500/10 font-semibold" : "text-zinc-500";
                          const propRateCls = rateChanged ? "text-cyan-300 bg-cyan-500/10 font-semibold" : "text-zinc-500";
                          const rmLbl = pl.rm_code || "—";
                          return (
                            <tr key={pl.line_no} className="border-t border-[var(--border)]" data-testid={`approvals-amend-line-${pl.line_no}`}>
                              <td className="px-2 py-2 font-mono text-zinc-500">{String(pl.line_no).padStart(3, "0")}</td>
                              <td className="px-2 py-2 font-mono text-[var(--accent)]">{rmLbl}<div className="text-[10px] text-zinc-500">{pl.rm_name || ""}</div></td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtInt(pl.qty_required)}</td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-zinc-400">{fmtInt(pl.current_qty_adjustment)}</td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-zinc-400">{fmtInt(pl.current_qty_ordered)}</td>
                              <td className={`px-2 py-2 text-right font-mono tabular-nums ${propTotCls}`} data-testid={`approvals-amend-line-${pl.line_no}-prop-total`}>{fmtInt(pl.proposed_qty_ordered)}</td>
                              <td className="px-2 py-2 text-right font-mono tabular-nums text-zinc-400">{pl.current_unit_cost ? `${ccySym}${Number(pl.current_unit_cost).toFixed(2)}` : "—"}</td>
                              <td className={`px-2 py-2 text-right font-mono tabular-nums ${propRateCls}`} data-testid={`approvals-amend-line-${pl.line_no}-prop-rate`}>{pl.proposed_unit_cost ? `${ccySym}${Number(pl.proposed_unit_cost).toFixed(2)}` : "—"}</td>
                              <td className="px-2 py-2 text-right font-mono text-zinc-500">{pl.uom || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="text-[10px] font-mono text-zinc-500 px-2 py-1.5 border-t border-[var(--border)] bg-[var(--bg-2)]/30">
                      Cyan cells = proposed change. Values are the canonical merge that will be applied on Authorise (byte-equal to Phase 2B.1 activation output).
                    </div>
                  </div>
                ) : (
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">{detail.doc_type === "PO" ? "RM Code" : "Product Code"}</th>
                        <th className="px-2 py-2 text-right">{isCommercialInv ? "Qty" : "Order Qty"}</th>
                        {detail.doc_type === "WO" && <th className="px-2 py-2 text-right" title="Rejection allowance % applied to Order Qty to derive the production buffer">Rej %</th>}
                        {!isCommercialInv && <th className="px-2 py-2 text-right" title="Over-Dispatch Buffer — technical cap, not a customer target">Buffer</th>}
                        {!isCommercialInv && <th className="px-2 py-2 text-right">Total</th>}
                        <th className="px-2 py-2 text-right">Rate</th>
                        <th className="px-2 py-2 text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((li, i) => {
                        const order = Number(li.order_qty ?? li.qty ?? 0);
                        const extra = Number(li.extra_qty ?? 0);
                        const total = Number(li.qty ?? (order + extra));
                        const rate = Number(li.rate ?? 0);
                        const value = Number(li.value ?? (total * rate));
                        return (
                          <tr key={i} className="border-t border-[var(--border)]">
                            <td className="px-2 py-2 font-mono text-zinc-500">{String(i + 1).padStart(3, "0")}</td>
                            <td className="px-2 py-2 font-mono text-[var(--accent)]">{li.product_code || "—"}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtInt(order)}</td>
                            {detail.doc_type === "WO" && <td className="px-2 py-2 text-right font-mono tabular-nums text-zinc-400" data-testid={`approvals-wo-line-${i}-rej-pct`}>{Number(li.rejection_allowance_pct || 0) ? `${Number(li.rejection_allowance_pct).toFixed(2)}%` : "—"}</td>}
                            {!isCommercialInv && <td className="px-2 py-2 text-right font-mono tabular-nums text-zinc-400" data-testid={`approvals-line-${i}-buffer`}>{fmtInt(extra)}</td>}
                            {!isCommercialInv && <td className="px-2 py-2 text-right font-mono tabular-nums text-white">{fmtInt(total)}</td>}
                            <td className="px-2 py-2 text-right font-mono tabular-nums">{rate ? `${ccySym}${rate.toFixed(rate < 10 ? 4 : 2)}` : "—"}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-300">{value ? `${ccySym}${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                          </tr>
                        );
                      })}
                      <tr className="border-t border-[var(--border)] bg-[var(--bg-2)]/40">
                        <td colSpan={isCommercialInv ? 2 : detail.doc_type === "WO" ? 5 : 4} className="px-2 py-2 text-right font-mono uppercase tracking-widest text-[10px] text-zinc-400">Totals</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold">{fmtInt(totQty)}</td>
                        <td></td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-emerald-300">{ccySym}{Number(totVal || 0).toLocaleString(ccySym === "₹" ? "en-IN" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                )}

                <DialogFooter className="gap-2 mt-4">
                  <button onClick={() => setDetail(null)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" data-testid="approvals-detail-close">Close</button>
                  <button onClick={() => { setRejectFor(detail); setRejectText(""); }} data-testid="approvals-detail-reject"
                    className="px-4 py-2 rounded-sm border border-red-500/50 text-red-300 hover:bg-red-500/10 text-sm font-semibold flex items-center gap-2">
                    <XCircleIcon size={14} weight="bold" />Reject with Comments
                  </button>
                  <button onClick={() => approve(detail)} data-testid="approvals-detail-approve" disabled={approveBlocked(detail)}
                    title={approveBlocked(detail) ? INTEGRITY_MESSAGE : undefined}
                    className="px-4 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600">
                    <CheckCircleIcon size={14} weight="bold" />Authorise
                  </button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reject-with-comments modal */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="approvals-reject-dialog" description="Management authorisation action — review, authorise or reject the pending document.">
          {rejectFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Reject {rejectFor.doc_no}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-zinc-400">
                {isAmendmentRow(rejectFor)
                  ? `Rejecting returns the amendment ${rejectFor.amendment && rejectFor.amendment.amendment_no} to Draft for correction. The effective PO stays Authorised at Revision ${Number((rejectFor.amendment && rejectFor.amendment.revision) || 0)} — proposed values are not promoted.`
                  : "Rejecting cancels the document and stamps your comments to its audit trail."}
              </div>
              <textarea autoFocus value={rejectText} onChange={e => setRejectText(e.target.value)} rows={4}
                placeholder="Why is this being rejected? (required)"
                className="w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm p-3 text-sm text-white focus:outline-none focus:border-red-500 mt-2"
                data-testid="approvals-reject-text" />
              <DialogFooter className="gap-2">
                <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
                <button onClick={submitReject} data-testid="approvals-reject-submit"
                  className="px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center gap-2">
                  <XCircleIcon size={14} weight="bold" />Confirm Reject
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
